// Figma: no frame provided for this piece — built using the established design system
// (tokens, card pattern from CampaignInfo.js/AlertsSection.js) per the "extrapolate" decision.
// LOGIC PORTED from LC2's commons/components/Offers/OffersTimeline.js:
// - `findMaxOverlaps` (the row-stacking algorithm that keeps offers within three weeks of
//   each other from sharing a row) is ported verbatim — this is the one piece of real
//   business logic in the file (everything else there is chart rendering).
// - The offer-bar dataset (id/start/end/label/color, defaulting a missing end date to
//   2099-12-31) and the "6 months before campaign start -> 12 months after today" timeline
//   window are ported the same way.
// VISUAL SIMPLIFICATION: LC2 rendered this with d3 (scaleTime/scaleBand/axisTop + a
// d3-drag pan handler). This app deliberately avoids adding new dependencies for something a
// plain approach can already do, so d3 was not added to LC-new — this is a from-scratch
// flexbox/CSS timeline: dates are mapped to pixels with a hand-rolled linear scale
// (`scaleX`), and instead of drag-to-pan the whole timeline range renders into a
// horizontally-scrollable track (native `overflow-x: auto`), auto-scrolled near "today" on
// mount. The d3 sub-axis of individual weekday tick marks is dropped in favor of simple
// month gridlines/labels plus a single "Today" marker.
import React, {useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {Box, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';
import {activeColorsObj} from '../../../commons/constants/offersConstants';

const ROW_HEIGHT = 40;
const PX_PER_DAY = 6;
const AXIS_HEIGHT = 28;
const THREE_WEEKS_MS = 3 * 7 * 24 * 60 * 60 * 1000;

// Ported verbatim from LC2's OffersTimeline.js: greedily assigns each offer to the first row
// where it doesn't land within three weeks of another offer already in that row, mutating
// `offer.row` in place. Returns the number of rows needed.
const findMaxOverlaps = (offers) => {
    offers.sort((a, b) => a.start - b.start);

    let placements = [];
    let maxRow = 0;

    offers.forEach((offer) => {
        let placed = false;
        for (let row = 0; row <= maxRow; row++) {
            if (!placements[row] || !placements[row].some((o) => o.end > offer.start - THREE_WEEKS_MS)) {
                if (!placements[row]) placements[row] = [];
                placements[row].push(offer);
                placed = true;
                break;
            }
        }
        if (!placed) {
            placements[++maxRow] = [offer];
        }
    });

    placements.forEach((row, rowIndex) => {
        row.forEach((offer) => {
            offer.row = rowIndex;
        });
    });

    return maxRow + 1;
};

const getRandomId = () => `offer-${Math.random().toString(36).slice(2)}`;

const OfferTimelineChart = ({offersObj, campaign}) => {
    const scrollRef = useRef(null);

    const offers = useMemo(() => {
        const formattedOffers = [];
        for (const offerType in offersObj) {
            for (const offer of offersObj[offerType]) {
                formattedOffers.push({
                    id: offer.id || getRandomId(),
                    start: offer.startDate,
                    end: offer.endDate ? offer.endDate : new Date('2099-12-31'),
                    label: offer.offerRewardTimelineValue,
                    color: offer.color
                });
            }
        }
        return formattedOffers.sort((a, b) => a.start - b.start);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(offersObj)]);

    const timelineStart = useMemo(() => {
        const campaignStartDate = campaign?.start_date || campaign?.createddate;
        const base = campaignStartDate ? dayjs(campaignStartDate) : dayjs();
        return base.subtract(6, 'month').startOf('month');
    }, [campaign]);

    const timelineEnd = useMemo(() => dayjs().add(12, 'month').endOf('month'), []);

    const numberOfRows = useMemo(() => findMaxOverlaps(offers), [offers]);

    const totalDays = Math.max(1, timelineEnd.diff(timelineStart, 'day'));
    const width = totalDays * PX_PER_DAY;
    const chartHeight = numberOfRows * ROW_HEIGHT;

    const scaleX = (date) => dayjs(date).diff(timelineStart, 'day') * PX_PER_DAY;

    const monthMarks = useMemo(() => {
        const marks = [];
        let cursor = timelineStart;
        while (cursor.isBefore(timelineEnd)) {
            marks.push(cursor);
            cursor = cursor.add(1, 'month');
        }
        return marks;
    }, [timelineStart, timelineEnd]);

    const today = dayjs();
    const todayX = scaleX(today);

    useEffect(() => {
        // Approximates LC2's default drag-window (roughly "2 months before today" centered in
        // view) by scrolling there once, instead of supporting an ongoing drag-to-pan gesture.
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = Math.max(0, scaleX(today.subtract(2, 'month')) - 40);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offers.length]);

    return (
        <Box ref={scrollRef} sx={{overflowX: 'auto', width: '100%'}}>
            <Box sx={{position: 'relative', width, minWidth: '100%', height: chartHeight + AXIS_HEIGHT}}>
                {monthMarks.map((mark, idx) => {
                    const x = scaleX(mark);
                    const nextX = idx < monthMarks.length - 1 ? scaleX(monthMarks[idx + 1]) : width;
                    return (
                        <Box key={mark.format('YYYY-MM')}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: x,
                                    top: AXIS_HEIGHT,
                                    bottom: 0,
                                    width: '1px',
                                    backgroundColor: tokens.color.neutral200
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    left: (x + nextX) / 2,
                                    top: 0,
                                    transform: 'translateX(-50%)',
                                    color: tokens.color.neutral600,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {mark.format('MMM YYYY')}
                            </Typography>
                        </Box>
                    );
                })}

                <Box
                    sx={{
                        position: 'absolute',
                        left: todayX,
                        top: AXIS_HEIGHT,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: tokens.color.sky600
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        left: todayX - 16,
                        top: 2,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: '4px',
                        backgroundColor: tokens.color.sky600
                    }}
                >
                    <Typography variant="caption" sx={{color: tokens.color.white, fontWeight: 600}}>
                        Today
                    </Typography>
                </Box>

                {offers.map((offer) => {
                    const left = scaleX(offer.start);
                    const barWidth = Math.max(4, scaleX(offer.end) - left);
                    return (
                        <Box
                            key={offer.id}
                            title={offer.label}
                            sx={{
                                position: 'absolute',
                                left,
                                width: barWidth,
                                top: AXIS_HEIGHT + offer.row * ROW_HEIGHT + 4,
                                height: ROW_HEIGHT - 8,
                                borderRadius: '8px',
                                backgroundColor: offer.color,
                                display: 'flex',
                                alignItems: 'center',
                                px: 1.25,
                                overflow: 'hidden'
                            }}
                        >
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{color: activeColorsObj[offer.color] || tokens.color.neutral700, fontWeight: 500}}
                            >
                                {offer.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

OfferTimelineChart.propTypes = {
    campaign: PropTypes.object,
    offersObj: PropTypes.shape({
        pastOffers: PropTypes.array,
        currentOffers: PropTypes.array,
        futureOffers: PropTypes.array
    })
};

const OfferTimeline = ({offersObj, campaign}) => {
    const hasOffers = Boolean(offersObj.pastOffers.length || offersObj.currentOffers.length || offersObj.futureOffers.length);

    return (
        <Box
            sx={{
                width: '100%',
                p: 2,
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: tokens.radius.card,
                backgroundColor: tokens.color.white
            }}
        >
            <Typography variant="subtitle1" fontWeight={600} sx={{mb: 2}}>
                Offer Timeline
            </Typography>
            {hasOffers ? (
                <OfferTimelineChart offersObj={offersObj} campaign={campaign} />
            ) : (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px'}}>
                    <Typography variant="body2" color="text.secondary">
                        No offers yet
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

OfferTimeline.propTypes = {
    campaign: PropTypes.object,
    offersObj: PropTypes.shape({
        pastOffers: PropTypes.array,
        currentOffers: PropTypes.array,
        futureOffers: PropTypes.array
    })
};

export default OfferTimeline;
